
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Info, Mail, CheckCircle, RefreshCw, Link as LinkIcon, AlertCircle, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAdminNotifications } from '@/hooks/useAdminNotifications';

interface IntegrationSettingsProps {
  onSave: () => void;
}

export function IntegrationSettings({ onSave }: IntegrationSettingsProps) {
  const { toast } = useToast();
  const { sendNotification, isSending } = useAdminNotifications();
  const [isLoading, setIsLoading] = useState(false);
  const [enableEmailService, setEnableEmailService] = useState(true);
  const [fromEmail, setFromEmail] = useState('notifications@drivecourses.com');
  const [domainVerified, setDomainVerified] = useState(true);
  const [isTestingSend, setIsTestingSend] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  
  // State for webhook settings
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  
  useEffect(() => {
    // Fetch settings from the database
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        setFetchError(null);
        
        // Fetch settings from Supabase
        const { data, error } = await supabase.functions.invoke('get-settings', {
          body: { type: 'email' }
        });
        
        if (error) throw error;
        
        if (data) {
          setEnableEmailService(data.enabled ?? true);
          setFromEmail(data.fromEmail || 'notifications@drivecourses.com');
          setDomainVerified(data.domainVerified ?? true);
          
          if (data.webhook) {
            setWebhookEnabled(data.webhook.enabled ?? false);
            setWebhookUrl(data.webhook.url || '');
            // Don't set webhook secret from response for security reasons
          }
        }
      } catch (error) {
        console.error('Error fetching integration settings:', error);
        setFetchError('Could not load integration settings. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  const handleSaveSettings = async () => {
    try {
      setIsLoading(true);
      
      // Save settings to Supabase
      const { error } = await supabase.functions.invoke('save-settings', {
        body: { 
          type: 'email',
          settings: {
            enabled: enableEmailService,
            fromEmail,
            webhook: {
              enabled: webhookEnabled,
              url: webhookUrl,
              // Only send secret if it was changed (not empty)
              ...(webhookSecret ? { secret: webhookSecret } : {})
            }
          }
        }
      });
      
      if (error) throw error;
      
      // Call onSave to trigger the toast notification
      onSave();
      toast({
        title: 'Settings saved',
        description: 'Your integration settings have been updated'
      });
    } catch (error) {
      console.error('Error saving integration settings:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not save integration settings'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleTestEmailConnection = async () => {
    setIsTestingSend(true);
    
    toast({
      title: 'Sending Test Email',
      description: 'Sending a test email via Resend...'
    });
    
    try {
      // Send an actual test email using the useAdminNotifications hook
      const result = await sendNotification({
        title: 'Test Email',
        message: 'This is a test email sent from the admin settings panel to verify email delivery.',
        eventType: 'test_event',
        metadata: {
          test: true,
          timestamp: new Date().toISOString()
        },
        showToast: false
      });
      
      if (result.success) {
        toast({
          title: 'Email Test Successful',
          description: 'The test email was sent successfully via Resend!'
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Email Test Failed',
          description: result.error || 'There was an error sending the test email'
        });
      }
    } catch (error) {
      console.error('Error testing email:', error);
      toast({
        variant: 'destructive',
        title: 'Email Test Failed',
        description: 'There was an unexpected error sending the test email'
      });
    } finally {
      setIsTestingSend(false);
    }
  };
  
  const handleGenerateWebhookSecret = () => {
    // Generate a random webhook secret
    const randomSecret = Array.from(window.crypto.getRandomValues(new Uint8Array(32)))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
      
    setWebhookSecret(randomSecret);
    
    toast({
      title: 'Secret Generated',
      description: 'A new webhook secret has been generated'
    });
  };

  if (fetchError) {
    return (
      <Alert variant="destructive" className="my-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{fetchError}</AlertDescription>
      </Alert>
    );
  }

  if (isLoading && !fetchError) {
    return (
      <div className="flex justify-center items-center py-16">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Email Service Configuration</CardTitle>
          <CardDescription>
            Configure the Resend email service for sending notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="mb-4 bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-800">Email Service Information</AlertTitle>
            <AlertDescription className="text-blue-700">
              Your system is configured to send emails using Resend. The API key is securely stored in Supabase secrets.
            </AlertDescription>
          </Alert>
          
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="email-service-enabled" className="text-base">Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Enable or disable the email notification service
              </p>
            </div>
            <Switch
              id="email-service-enabled"
              checked={enableEmailService}
              onCheckedChange={setEnableEmailService}
              disabled={isLoading}
            />
          </div>
          
          {enableEmailService && (
            <div className="space-y-4 pt-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-sm font-medium">Sending Domain</h3>
                  <p className="text-xs text-muted-foreground">drivecourses.com</p>
                </div>
                <Badge className="bg-green-600">
                  <CheckCircle className="h-3 w-3 mr-1" /> Verified
                </Badge>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="from-email">From Email Address</Label>
                <Input 
                  id="from-email" 
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground">
                  The email address that will appear in the "From" field, must use the verified domain
                </p>
              </div>
              
              <Button 
                variant="outline" 
                onClick={handleTestEmailConnection}
                disabled={isLoading || isTestingSend || !enableEmailService}
                className="flex items-center"
              >
                {isTestingSend ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                {isTestingSend ? 'Sending...' : 'Send Test Email'}
              </Button>
              
              <div className="flex items-center mt-2">
                <LinkIcon className="h-4 w-4 mr-2 text-blue-600" />
                <a 
                  href="https://resend.com/domains" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-sm text-blue-600 hover:underline"
                >
                  Manage domains in Resend dashboard
                </a>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Webhook Integration</CardTitle>
          <CardDescription>
            Configure webhooks to notify external systems about events
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Information</AlertTitle>
            <AlertDescription>
              Webhooks allow external systems to be notified when certain events occur in the certificate system.
            </AlertDescription>
          </Alert>
          
          <div className="flex items-center justify-between pt-2">
            <div>
              <Label htmlFor="webhook-enabled" className="text-base">Enable Webhooks</Label>
              <p className="text-sm text-muted-foreground">
                Send event notifications to external systems
              </p>
            </div>
            <Switch
              id="webhook-enabled"
              checked={webhookEnabled}
              onCheckedChange={setWebhookEnabled}
              disabled={isLoading}
            />
          </div>
          
          {webhookEnabled && (
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="webhook-url">Webhook URL</Label>
                <Input 
                  id="webhook-url" 
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://"
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground">
                  The URL to which webhook notifications will be sent
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="webhook-secret">Webhook Secret</Label>
                <div className="flex space-x-2">
                  <Input 
                    id="webhook-secret" 
                    value={webhookSecret}
                    onChange={(e) => setWebhookSecret(e.target.value)}
                    type="password"
                    placeholder="••••••••"
                    disabled={isLoading}
                    className="flex-1"
                  />
                  <Button 
                    variant="outline" 
                    onClick={handleGenerateWebhookSecret}
                    disabled={isLoading}
                  >
                    Generate
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  This secret is used to verify webhook payloads
                </p>
              </div>
              
              <Separator className="my-4" />
              
              <div className="space-y-2">
                <Label>Available Events</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="flex items-center space-x-2">
                    <Switch id="event-certificate-issued" defaultChecked />
                    <Label htmlFor="event-certificate-issued">Certificate Issued</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch id="event-certificate-revoked" defaultChecked />
                    <Label htmlFor="event-certificate-revoked">Certificate Revoked</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch id="event-report-generated" defaultChecked />
                    <Label htmlFor="event-report-generated">Report Generated</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch id="event-retention-executed" defaultChecked />
                    <Label htmlFor="event-retention-executed">Retention Executed</Label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      <div className="flex justify-end">
        <Button onClick={handleSaveSettings} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Integration Settings'}
        </Button>
      </div>
    </div>
  );
}
